import { useCallback, useEffect, useRef } from 'react'
import { reloadIfStale } from '../util/appBuild'

// Guards the event submit path against a STALE browser bundle. A pre-redesign
// tab submits the old format, which bakes the wall-clock in the browser's zone
// instead of the venue's — the cross-zone "an hour off" bug the
// server-authoritative redesign fixes. Since the server can't recover the
// intended time from an already-baked instant, we refuse to submit on a stale
// tab and reload to the current build instead.
//
// Two checks, both fail-open (see appBuild.ts):
//   * on mount — reloads early when a stale tab navigates to the form (the
//     common case), while the form is still empty, so no input is lost.
//   * before submit — covers a deploy that lands while the form is already
//     open. Rare, and reloading discards the in-progress entry, but that is
//     strictly better than silently storing a wrong time.
//
// CVL-126: a form that closes while the before-submit check is still running
// (the person went to another page) drops the submit instead of saving later.
//
// Returns a wrapper for the form's submit handler.
const useFreshBuildSubmit = (): ((proceed: () => void) => Promise<void>) => {
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    void reloadIfStale()
    return () => {
      mounted.current = false
    }
  }, [])

  return useCallback(async (proceed: () => void): Promise<void> => {
    if (await reloadIfStale()) return // stale -> reloading; do NOT submit
    if (!mounted.current) return // the form closed while we waited; do NOT submit
    proceed()
  }, [])
}

export default useFreshBuildSubmit
